// Developers - one implementation path: install, CSP when needed, blocking,
// annotation, browser API, Consent Mode, SPAs, then troubleshooting.

import { FAQ_BLOCK_TYPES, FAQ_SECTION_ICON_TONES } from 'src/services/consts/faq/content.const'

/** @type {import('src/services/helpers/faq/content.helper').FaqTopic} */
export const DEVELOPERS_TOPIC = {
  id: 'developers',
  title: 'Developers',
  icon: 'code',
  color: 'violet-muted',
  fill: 'violet-light',
  description:
    'Place the generated tag, confirm the primary Website URL, then connect blocking, annotation, the browser API, and Consent Mode.',
  sections: [
    {
      title: 'Install and verify',
      items: [{ articleId: 'install-the-script', icon: 'code' }],
    },
    {
      title: 'Blocking and annotation',
      items: [
        { articleId: 'how-blocking-works', icon: 'shield-off' },
        { articleId: 'annotate-your-scripts', icon: 'pencil' },
      ],
    },
    {
      title: 'API, storage, and Consent Mode',
      items: [
        { articleId: 'javascript-api', icon: 'book-open' },
        { articleId: 'google-consent-mode-developers', icon: 'globe' },
      ],
    },
    {
      title: 'Site integration',
      items: [
        { articleId: 'content-security-policy', icon: 'lock' },
        { articleId: 'single-page-apps', icon: 'route' },
        { articleId: 'developer-troubleshooting', icon: 'wrench' },
      ],
    },
  ],
}

/** @type {Record<string, import('src/services/helpers/faq/content.helper').FaqArticle>} */
export const DEVELOPERS_ARTICLES = {
  'install-the-script': {
    id: 'install-the-script',
    title: 'Install the script',
    description:
      'Copy the generated Cookie Armor tag into the head first, without async, defer, or a tag manager, then confirm it with a fresh check of the primary Website URL.',
    keywords: [
      'script missing',
      'generated tag',
      'install snippet',
      'bootstrap.js',
      'cookiearmor-bootstrap',
      'YOUR_SITE_ID',
      'head tag',
      'GTM load order',
      'Google Tag Manager',
      'async defer',
      'Next.js beforeInteractive',
      'Nuxt useHead',
      'Script Installed',
      'Script Missing',
      "Couldn't check",
      'Access blocked',
      'Verify installation',
      'Recheck installation',
      'install status',
      'WAF',
      'bot protection',
      'cdn.cookiearmor.com',
      'domain is not allowed',
      'www stripped',
      'additional domains',
      'alias domains',
      'allowlist',
      'primary Website URL',
      'Content Security Policy',
      'CSP',
    ],
    lastUpdated: '2026-09-27',
    content: [
      {
        id: 'its-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "Place this banner's generated tag first in `<head>` on every page so tags that follow can wait for a choice. A tag that already ran cannot be called back.",
      },
      {
        id: 'its-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'If the site already sends a Content Security Policy (CSP), allow Cookie Armor before you add the tag. [[Content Security Policy|content-security-policy]]',
      },
      {
        id: 'its-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Copy the tag from this banner. In the examples on this page, `YOUR_SITE_ID` is only a stand-in. Do not type a URL in its place.',
      },
      {
        id: 'its-video-section',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Watch the install',
        icon: 'file-video',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'its-video-lead',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The steps below are complete without the video. The video shows the same placement and the primary Website URL check.',
          },
          {
            id: 'its-video',
            type: FAQ_BLOCK_TYPES.VIDEO,
            videoId: '1223430873',
            hash: '36f838aff4',
            title: 'Place the generated tag and confirm the primary Website URL',
          },
        ],
      },
      {
        id: 'its-boot',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Where the tag goes',
        icon: 'rocket',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'its-boot-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The generated tag omits `async` and `defer` on purpose. Do not add either, and do not load Cookie Armor through Google Tag Manager (GTM) or another tag manager. Put this tag first, then the tag manager.',
          },
          {
            id: 'its-boot-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'One tag per banner',
            text: "Each banner has its own generated tag. Do not reuse another banner's tag.",
          },
        ],
      },
      {
        id: 'its-frameworks',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Generated tags',
        icon: 'code',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'its-frameworks-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'its-frameworks-html',
                text: 'HTML, React (`public/index.html`), and Vue (`index.html`) use a blocking `<script id="cookiearmor-bootstrap">` in `<head>`.',
              },
              {
                id: 'its-frameworks-next',
                text: 'Next.js uses `next/script` with `strategy="beforeInteractive"`.',
              },
              {
                id: 'its-frameworks-nuxt',
                text: 'Nuxt uses `useHead` in `app.vue`.',
              },
              {
                id: 'its-frameworks-async',
                text: 'None of those generated tags set `async` or `defer`. The file they load is `bootstrap.js` on `https://cdn.cookiearmor.com`.',
              },
              {
                id: 'its-frameworks-spa',
                text: 'Client-routed apps boot once. [[Single-page apps|single-page-apps]]',
              },
            ],
          },
          {
            id: 'its-copy-tabs',
            type: FAQ_BLOCK_TYPES.CODE_TABS,
            variant: 'install',
          },
        ],
      },
      {
        id: 'its-hosts',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'When production updates',
        icon: 'link',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'its-hosts-url',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A successful **Create**, Overview **Save**, or Behaviour **Publish changes** updates production immediately. The next page load uses that result. Copy the generated tag again only when you need the markup - you do not assemble a new URL after those saves.',
          },
        ],
      },
      {
        id: 'its-domain',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Which hosts may load the tag',
        icon: 'badge-check',
        color: FAQ_SECTION_ICON_TONES.POSITIVE,
        blocks: [
          {
            id: 'its-domain-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'its-domain-allowlist',
                text: 'Cookie Armor allows the primary **Website URL** and any **Additional Domains** on that banner.',
              },
              {
                id: 'its-domain-primary',
                text: 'The primary **Website URL** matches the page after a leading `www.` is stripped. Protocol and port must also match.',
              },
              {
                id: 'its-domain-example',
                text: '`https://example.com` does not allow `http://example.com` or `https://example.com:8443`.',
              },
              {
                id: 'its-domain-additional',
                text: '**Additional Domains** are hostnames. They match after the same `www.` strip, with any protocol or port.',
              },
              {
                id: 'its-domain-exit',
                text: 'If the page host is not on that list, Cookie Armor stops and the console reports that the current domain is not allowed.',
              },
              {
                id: 'its-domain-fix',
                text: 'Add or correct the host when you [[create a banner|create-a-banner]]. Later host edits use Overview **Save**. If the host should already be allowed, [[contact support|contact-support]].',
              },
            ],
          },
          {
            id: 'its-domain-dev-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "Which hosts may load the tag is separate from where a visitor's choice is stored. [[JavaScript API|javascript-api]]",
          },
        ],
      },
      {
        id: 'its-verify',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Confirm the primary Website URL',
        icon: 'search-check',
        color: FAQ_SECTION_ICON_TONES.BRAND,
        blocks: [
          {
            id: 'its-verify-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Verify installation** and **Recheck installation** each run a fresh crawl of the primary **Website URL** only. They do not check **Additional Domains**.',
          },
          {
            id: 'its-verify-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Status', 'What it means'],
            rows: [
              [
                '{status:script-installed}',
                'The check reached the primary Website URL and found the Cookie Armor tag.',
              ],
              [
                '{status:script-missing}',
                'The check reached the primary Website URL and did not find the tag.',
              ],
              [
                '{status:couldnt-check}',
                'The check could not finish (for example **Site not found.**).',
              ],
              [
                '{status:access-blocked}',
                'The check reached the site and was refused (for example a WAF or `403`). The tag may already be on the page.',
              ],
            ],
            caption:
              'Install-status meanings for the primary Website URL. Access blocked is not Script Missing.',
          },
          {
            id: 'its-verify-limits',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '**Script Installed** does not prove that **Additional Domains** are allowed, that a visitor can store a choice, that other tags are held, or that a Content Security Policy allows Cookie Armor.',
          },
          {
            id: 'its-verify-browser',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'On a page that loaded the tag, `window.__cookiearmor__` is present. What you can call: [[JavaScript API|javascript-api]]. If the browser blocks the request, check `script-src`. [[Content Security Policy|content-security-policy]]',
          },
          {
            id: 'its-verify-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Next, see what that tag holds and what it leaves running. [[How blocking works|how-blocking-works]]',
          },
        ],
      },
    ],
  },

  'how-blocking-works': {
    id: 'how-blocking-works',
    title: 'How blocking works',
    description:
      "Cookie Armor holds a classified script, iframe, or pixel until the visitor allows it, and a valid data-cookiearmor-category is that tag's only category.",
    keywords: [
      'scripts not blocked',
      'tags still firing',
      'text/plain',
      'unclassified allowed',
      'HttpOnly',
      'about:blank',
      'multi-category',
      'data-cookiearmor-category',
      'data-cookiearmor-src',
      'static iframe',
      'tracking pixel',
      'unclassified cookie',
      'alias domains',
      'Necessary',
      'Google Consent Mode',
      'GCM',
    ],
    lastUpdated: '2026-10-26',
    content: [
      {
        id: 'hbw-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Cookie Armor can hold a **script**, **iframe**, or **pixel** after that tag has a category. An unclassified tag runs. [[Unclassified cookies|unclassified-cookies]]',
      },
      {
        id: 'hbw-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'A scan may assign several categories to one tag. Cookie Armor holds that tag until the visitor grants every one of those categories. A valid attribute follows a different rule. [[Annotate your scripts|annotate-your-scripts]]',
      },
      {
        id: 'hbw-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Category meanings: [[Cookie categories|cookie-categories]]. The tag has to be first in the page: [[Install the script|install-the-script]].',
      },
      {
        id: 'hbw-what',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What is held',
        icon: 'funnel',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'hbw-what-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Target', 'Held?', 'Rule'],
            rows: [
              [
                '`<script src>`',
                'When classified',
                'From the scan, or from a valid `data-cookiearmor-category`. [[Annotate your scripts|annotate-your-scripts]]',
              ],
              [
                'Inline `<script>`',
                'When classified',
                'From the scan, or from your annotation. A static inline script in the HTML must use `type="text/plain"` or the browser runs it first.',
              ],
              [
                '`<iframe>`',
                'When classified',
                'From the scan, or from your annotation. A static iframe in the HTML must put its address in `data-cookiearmor-src` and carry no `src`, or the browser requests it first. `about:blank` is never held. [[Annotate your scripts|annotate-your-scripts]]',
              ],
              [
                '`<img>`',
                'When classified',
                'From the scan, or from your annotation. A tracking pixel is this image. A static image in the HTML must put its address in `data-cookiearmor-src` and carry no `src`.',
              ],
              ['`necessary`', 'Never', 'A Necessary-only requirement is never held.'],
              [
                'Unclassified tag',
                'Never',
                'No scan category and no valid annotation. The tag runs. [[Unclassified cookies|unclassified-cookies]]',
              ],
              [
                'Google Consent Mode hosts',
                'Never',
                'Never held as scripts, including when Google Consent Mode (GCM) is off. [[Google Consent Mode for developers|google-consent-mode-developers]]',
              ],
              [
                'Inline gtag config',
                'Never',
                'An inline script whose body contains `function gtag(){dataLayer.push(arguments);}` is never held, even when `data-cookiearmor-category` is set.',
              ],
              [
                'Cookie Armor CDN files',
                'Never',
                'Files on `https://cdn.cookiearmor.com` are never held.',
              ],
              [
                'HttpOnly cookies',
                'Never',
                'Not readable from the page, and not cleared when a category is denied.',
              ],
              [
                'Already running',
                'Never',
                'A tag that ran before Cookie Armor started cannot be called back. [[Install the script|install-the-script]]',
              ],
              [
                'Bundled first-party code',
                'Never',
                'Tracking compiled into your own JavaScript is not a separate tag.',
              ],
            ],
            caption: 'What Cookie Armor holds, and the limits that matter on the live site.',
          },
          {
            id: 'hbw-what-hosts',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "Scan rules for your own site's scripts, iframes, and pixels apply on every host the site is served from: the primary **Website URL**, its `www.` variant, and each **Additional Domain**. [[Install the script|install-the-script]]",
          },
          {
            id: 'hbw-what-success',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A successful check is specific. Classified optional scripts, iframes, and pixels wait. Necessary tags, tags with no category, Google Consent Mode hosts, tags that already ran, and tracking bundled into your own JavaScript can still run.',
          },
        ],
      },
      {
        id: 'hbw-multi',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Several scan categories, or one attribute',
        icon: 'package-open',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'hbw-multi-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When the scan assigns more than one category, granting one of them is not enough. The tag stays held until every scanned category is granted.',
          },
          {
            id: 'hbw-multi-unclassified',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Only classified categories count. A cookie or storage key the scan could not classify, written by a script, iframe, or pixel that does have a category, never adds a requirement. A visitor who allowed Marketing alone still gets a Marketing script that also writes an unclassified cookie. A tag with no classified category at all still runs. [[Unclassified cookies|unclassified-cookies]]',
          },
          {
            id: 'hbw-multi-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A valid `data-cookiearmor-category` is the only category for that tag, and scan categories are not added to it. [[Annotate your scripts|annotate-your-scripts]]',
          },
        ],
      },
    ],
  },

  'annotate-your-scripts': {
    id: 'annotate-your-scripts',
    title: 'Annotate your scripts',
    description:
      'Set data-cookiearmor-category to necessary, analytics, marketing, or preferences so a script, iframe, or pixel the scan missed can wait for a choice. An iframe or pixel written in the HTML puts its address in data-cookiearmor-src.',
    keywords: [
      'data-cookiearmor-category',
      'data-cookiearmor-src',
      'text/plain',
      'manual annotation',
      'inline script',
      'iframe',
      'static iframe',
      'tracking pixel',
      'static pixel',
      'script src',
      'necessary',
      'analytics',
      'marketing',
      'preferences',
      'invalid',
    ],
    lastUpdated: '2026-10-26',
    content: [
      {
        id: 'ays-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "**Annotation** is the `data-cookiearmor-category` attribute. Use it when a scan did not classify a script, iframe, or pixel, or when that tag's address changes often enough that the scan is not enough.",
      },
      {
        id: 'ays-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'What a valid category does once it is set: [[How blocking works|how-blocking-works]].',
      },
      {
        id: 'ays-values',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'The four values',
        icon: 'clipboard-list',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'ays-values-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '`data-cookiearmor-category` accepts only `necessary`, `analytics`, `marketing`, or `preferences`. Any other value is ignored, and normal scan matching still applies. If there is no scan category and no valid attribute, the tag runs. [[Unclassified cookies|unclassified-cookies]] Category meanings: [[Cookie categories|cookie-categories]].',
          },
          {
            id: 'ays-values-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'One category for that tag',
            text: 'A valid attribute is the only category for that tag. Scan categories are not added to it.\nThe attribute applies to `<script src>` as well as inline scripts, iframes, and images.',
          },
        ],
      },
      {
        id: 'ays-script',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'External script',
        icon: 'external-link',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'ays-script-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Put the attribute on the `<script src>` tag. A scan category is not required when the value is valid.',
          },
          {
            id: 'ays-script-tabs',
            type: FAQ_BLOCK_TYPES.CODE_TABS,
            tabs: [
              {
                id: 'before',
                label: 'Before',
                language: 'html',
                filename: 'before.html',
                code: '<script src="https://connect.facebook.net/en_US/fbevents.js"></script>',
              },
              {
                id: 'after',
                label: 'After',
                language: 'html',
                filename: 'after.html',
                code: '<script\n  src="https://connect.facebook.net/en_US/fbevents.js"\n  data-cookiearmor-category="marketing"\n></script>',
              },
            ],
          },
        ],
      },
      {
        id: 'ays-inline',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Inline script',
        icon: 'file-text',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'ays-inline-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A static `<script>` in the HTML can run before Cookie Armor starts. Set `type="text/plain"` and the category yourself.',
          },
          {
            id: 'ays-inline-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'After the visitor grants that category, Cookie Armor runs the script. A script you insert after Cookie Armor has started needs only the attribute.',
          },
          {
            id: 'ays-inline-tabs',
            type: FAQ_BLOCK_TYPES.CODE_TABS,
            tabs: [
              {
                id: 'static',
                label: 'Static HTML',
                language: 'html',
                filename: 'inline.html',
                code: '<script type="text/plain" data-cookiearmor-category="analytics">\n  initMyAnalytics();\n</script>',
              },
              {
                id: 'dynamic',
                label: 'Dynamic insert',
                language: 'js',
                filename: 'insert.js',
                code: "const s = document.createElement('script');\ns.setAttribute('data-cookiearmor-category', 'marketing');\ns.textContent = 'window.myTag = true;';\ndocument.head.appendChild(s);",
              },
            ],
          },
        ],
      },
      {
        id: 'ays-embed',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Iframe and tracking pixel',
        icon: 'layout-panel-top',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'ays-embed-p0',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The browser requests an `<iframe>` or `<img>` written in the HTML while it parses the page, before Cookie Armor can hold it. For a pixel or an embed, that request is the tracking event. Put the address in `data-cookiearmor-src` and leave `src` off, the same way a static inline script uses `type="text/plain"`.',
          },
          {
            id: 'ays-embed-p0b',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Cookie Armor classifies that address like a `src`: your `data-cookiearmor-category` wins, otherwise the scan decides. It loads the address at once when nothing holds it or the visitor has already allowed the category, and waits for the choice otherwise. The scan also reads `data-cookiearmor-src`, so these embeds still appear in scan results.',
          },
          {
            id: 'ays-embed-tabs',
            type: FAQ_BLOCK_TYPES.CODE_TABS,
            tabs: [
              {
                id: 'iframe',
                label: 'Iframe',
                language: 'html',
                filename: 'iframe.html',
                code: '<iframe\n  data-cookiearmor-src="https://www.youtube-nocookie.com/embed/VIDEO_ID"\n  data-cookiearmor-category="marketing"\n  width="560"\n  height="315"\n  title="Video"\n></iframe>',
              },
              {
                id: 'pixel',
                label: 'Pixel',
                language: 'html',
                filename: 'pixel.html',
                code: '<img\n  data-cookiearmor-src="https://example.com/pixel.gif"\n  data-cookiearmor-category="analytics"\n  width="1"\n  height="1"\n  alt=""\n/>',
              },
              {
                id: 'dynamic',
                label: 'Dynamic insert',
                language: 'js',
                filename: 'insert-pixel.js',
                code: "const img = new Image(1, 1);\nimg.setAttribute('data-cookiearmor-category', 'analytics');\nimg.src = 'https://example.com/pixel.gif';\ndocument.body.appendChild(img);",
              },
            ],
          },
          {
            id: 'ays-embed-js',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'An iframe or image your JavaScript inserts (`createElement`, `img.src = ...`, `appendChild`) keeps using `src` and needs no change.',
          },
          {
            id: 'ays-embed-rules',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'ays-embed-rules-set',
                text: 'Set `data-cookiearmor-src` in the markup, or before you insert the element. It is read when the element is inserted and on every consent change, not on an element already in the page.',
              },
              {
                id: 'ays-embed-rules-both',
                text: 'If an element has both `src` and `data-cookiearmor-src`, the live `src` wins and the attribute is ignored.',
              },
              {
                id: 'ays-embed-rules-none',
                text: 'With no `data-cookiearmor-category` and no scan category, the address loads at once. [[Unclassified cookies|unclassified-cookies]]',
              },
            ],
          },
          {
            id: 'ays-embed-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'A held iframe can show a placeholder that calls `openBanner()`. A held image does not. [[JavaScript API|javascript-api]]',
          },
          {
            id: 'ays-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Next, read or open that choice from your own code. [[JavaScript API|javascript-api]]',
          },
        ],
      },
    ],
  },

  'javascript-api': {
    id: 'javascript-api',
    title: 'JavaScript API',
    description:
      'window.__cookiearmor__ exposes getConsent, hasConsented, onConsentChange, offConsentChange, openBanner, and dismissBanner, and stores the choice for 365 days in host-only cookiearmor_consent_{siteId}.',
    keywords: [
      '__cookiearmor__',
      'getConsent',
      'hasConsented',
      'onConsentChange',
      'offConsentChange',
      'openBanner',
      'dismissBanner',
      'manage cookies',
      'Your Privacy Choices',
      'cookiearmor_consent',
      'localStorage',
      'SameSite',
      'host-only',
      'subdomain',
      'consent expiry',
      '365 days',
      'consent storage',
      'storage purge',
      'indexedDB',
      'Global Privacy Control',
      'GPC',
    ],
    lastUpdated: '2026-09-27',
    content: [
      {
        id: 'jsa-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'The **JavaScript API** is the set of calls on `window.__cookiearmor__`. Use it to read the current effective state, listen for a later choice, or open and dismiss the banner.',
      },
      {
        id: 'jsa-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'It is available once Cookie Armor has started, including before the banner is on screen. If your code can run first, guard with `if (window.__cookiearmor__)`.',
      },
      {
        id: 'jsa-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'These six methods are the whole public API. Category meanings: [[Cookie categories|cookie-categories]].',
      },
      {
        id: 'jsa-methods',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Methods',
        icon: 'wrench',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'jsa-lifecycle',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Once Cookie Armor has finished starting, call `getConsent()` to apply the current effective state, then use `onConsentChange()` for later visitor submissions. A stored choice is already reflected in `getConsent()` and is not replayed to the listener. On a visit where no banner is shown, `getConsent()` can be all granted while `hasConsented()` stays false, because no choice was stored.',
          },
          {
            id: 'jsa-methods-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Method', 'Arguments', 'Returns', 'Behaviour'],
            rows: [
              [
                '`getConsent()`',
                'None',
                '`{ necessary, analytics, marketing, preferences }` (booleans)',
                'Effective state. Before a stored choice this is the regulation default: optional categories denied for GDPR; granted for Cookie Notice; granted for CCPA unless Global Privacy Control (GPC) is on. `necessary` is always `true`.',
              ],
              [
                '`hasConsented()`',
                'None',
                '`boolean`',
                '`true` when a choice is stored for the **active** regulation. `false` before interaction, including a first CCPA visit while the allow-first default is in effect. A record stored under a different regulation is ignored.',
              ],
              [
                '`onConsentChange(fn)`',
                '`fn(categories)`',
                '`undefined`',
                'Fires after the visitor submits a choice (accept all, reject all, or a custom set). One listener only; a later call replaces the previous listener. Does not fire for a choice that was already stored on load.',
              ],
              ['`offConsentChange()`', 'None', '`undefined`', 'Removes the current listener.'],
              [
                '`openBanner()`',
                'None',
                '`undefined`',
                'Opens the banner and hides the cookie button. Does nothing if the banner is already visible. Loads the banner if it is not loaded yet. Reopened UI reflects the stored choice for the active regulation.',
              ],
              [
                '`dismissBanner()`',
                'None',
                '`undefined`',
                'Hides the banner and shows the cookie button without saving a choice. Same effect as Cookie Notice **Close**.',
              ],
            ],
            caption: 'Public surface of window.__cookiearmor__. No other methods are supported.',
          },
          {
            id: 'jsa-privacy',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'On a visit where Cookie Armor normally shows the banner or the cookie button, call `openBanner()` from your own **Privacy choices** control after `window.__cookiearmor__` exists. It opens the saved choice or the first-visit banner, and it does not save a choice itself.',
          },
          {
            id: 'jsa-privacy-code',
            type: FAQ_BLOCK_TYPES.CODE,
            language: 'js',
            filename: 'privacy-choices.js',
            code: 'if (window.__cookiearmor__) {\n  window.__cookiearmor__.openBanner();\n}',
          },
        ],
      },
      {
        id: 'jsa-storage',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Where the choice is stored',
        icon: 'cookie',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'jsa-storage-list',
            type: FAQ_BLOCK_TYPES.BULLETS,
            items: [
              {
                id: 'jsa-storage-write',
                text: 'When the visitor submits a choice, Cookie Armor writes one record to `localStorage` and mirrors it in a first-party cookie.',
              },
              {
                id: 'jsa-storage-key',
                text: 'Both use `cookiearmor_consent_{siteId}`.',
              },
              {
                id: 'jsa-storage-cookie',
                text: 'The cookie is `path=/; SameSite=Lax` with no `Domain` attribute, so it is host-only.',
              },
              {
                id: 'jsa-storage-age',
                text: 'It lasts 365 days.',
              },
              {
                id: 'jsa-storage-notice',
                text: 'Cookie Notice **Close** and `dismissBanner()` write nothing.',
              },
            ],
          },
          {
            id: 'jsa-storage-sub',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: '`localStorage` is limited to the page origin. The cookie is host-only, so `www.example.com`, `example.com`, and `app.example.com` do not share a choice.',
          },
          {
            id: 'jsa-storage-sub-3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Which hosts may load the tag is a separate rule. [[Install the script|install-the-script]]',
          },
        ],
      },
      {
        id: 'jsa-purge',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What a denied category clears',
        icon: 'trash',
        color: FAQ_SECTION_ICON_TONES.NEGATIVE,
        blocks: [
          {
            id: 'jsa-purge-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'When the visitor submits a choice, Cookie Armor clears cookies and web storage from your scan results whose category is denied.',
          },
          {
            id: 'jsa-purge-p1b',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'It expires first-party cookies visible to `document.cookie`, removes matching `localStorage` and `sessionStorage` keys, and deletes matching `indexedDB` databases on the **current origin**.',
          },
          {
            id: 'jsa-purge-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Storage on another origin, including inside an iframe on another origin, cannot be cleared from your page. **HttpOnly** cookies are not readable from the page and are not cleared this way.',
          },
          {
            id: 'jsa-purge-when',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The same clear also runs on page load when a stored choice already exists (GDPR and CCPA), and on a first CCPA visit when Global Privacy Control (GPC) is on.',
          },
          {
            id: 'jsa-purge-when-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "Cookie Notice never holds tags or clears storage - every category stays granted. Rejecting a category after an accept clears that category's scanned cookies and storage again.",
          },
          {
            id: 'jsa-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Google tags read a different signal. [[Google Consent Mode for developers|google-consent-mode-developers]]',
          },
        ],
      },
    ],
  },

  'google-consent-mode-developers': {
    id: 'google-consent-mode-developers',
    title: 'Google Consent Mode for developers',
    description:
      'When Google Consent Mode is on, the initial default denies analytics, ad, and preferences signals, the later update applies the effective category state, and ad data redaction starts on and then follows marketing.',
    keywords: [
      'GCM',
      'Google Consent Mode',
      'gtag',
      'analytics_storage',
      'ad_storage',
      'ad_user_data',
      'ad_personalization',
      'functionality_storage',
      'personalization_storage',
      'wait_for_update',
      'ads_data_redaction',
      'security_storage',
      'googletagmanager',
    ],
    lastUpdated: '2026-09-27',
    content: [
      {
        id: 'gcmd-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Google tags read Consent Mode signals (`analytics_storage`, `ad_storage`, and the rest), not Cookie Armor category names. When the setting is on, Cookie Armor starts from a denied default and sends the visitor result later.',
      },
      {
        id: 'gcmd-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'What Consent Mode is: [[What is Google Consent Mode?|what-is-google-consent-mode]]. Whether to turn it on: [[Google Consent Mode setting|google-consent-mode-setting]].',
      },
      {
        id: 'gcmd-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "When the setting is off, Cookie Armor emits no consent writes. Google's reference: [[Consent Mode on Google Developers|https://developers.google.com/tag-platform/security/guides/consent]].",
      },
      {
        id: 'gcmd-map',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Category to signal map',
        icon: 'route',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'gcmd-map-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Cookie Armor category', 'Consent Mode signals', 'Granted when'],
            rows: [
              [
                '`analytics`',
                '`analytics_storage`',
                'The initial default denies this signal. The later update sets it from the effective analytics state.',
              ],
              [
                '`marketing`',
                '`ad_storage`, `ad_user_data`, `ad_personalization`',
                'The initial default denies these signals. The later update sets them from the effective marketing state.',
              ],
              [
                '`preferences`',
                '`functionality_storage`, `personalization_storage`',
                'The initial default denies these signals. The later update sets them from the effective preferences state.',
              ],
              ['(none)', '`security_storage`', 'Always granted. Not tied to a category.'],
            ],
            caption:
              'The later update uses the effective category state. The initial default has already denied the optional signals.',
          },
          {
            id: 'gcmd-map-redact',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "After the visitor's country is resolved, and again after their choices, Cookie Armor sends this map from the effective category state. [[JavaScript API|javascript-api]]",
          },
        ],
      },
      {
        id: 'gcmd-timing',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Denied default, then the update',
        icon: 'arrow-right',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'gcmd-timing-steps',
            type: FAQ_BLOCK_TYPES.STEPS,
            items: [
              {
                id: 'gcmd-step-default',
                title: 'Start denied',
                text: 'When Google Consent Mode is enabled, the initial default denies all six optional signals: `analytics_storage`, `ad_storage`, `ad_user_data`, `ad_personalization`, `functionality_storage`, and `personalization_storage`. It grants `security_storage`, sets `wait_for_update` to `500`, and sets `ads_data_redaction` to `true`. The first denied default is the same for every visitor. Once the country is known, the later update applies the effective regulation and category state.',
              },
              {
                id: 'gcmd-step-update',
                title: 'Send the result later',
                text: "After the visitor's country is resolved, Cookie Armor sends an update from the effective category state, and it sends that update again after each choice. Before a click, Cookie Notice and CCPA can grant the optional signals, CCPA with Global Privacy Control (GPC) keeps them denied, and GDPR keeps optional signals denied until the visitor allows them. [[JavaScript API|javascript-api]] [[Choose your regulations|choose-your-regulations]] `functionality_storage` and `personalization_storage` follow preferences in that state. `ads_data_redaction` stays on while marketing is denied, and turns off when marketing is allowed.",
              },
            ],
          },
          {
            id: 'gcmd-expect',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "Denied means Google cannot use cookies for that purpose, not necessarily that no request occurs. Because Google tags are allowed to load, Google may send cookieless measurements or consent-state pings. `wait_for_update: 500` gives those tags up to 500 milliseconds to wait for Cookie Armor's update, after the country is known, before they send data.",
          },
          {
            id: 'gcmd-timing-shape',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The later update uses this shape. Ad data redaction is set from the marketing choice immediately before that update. It is not a default for you to paste.',
          },
          {
            id: 'gcmd-timing-code',
            type: FAQ_BLOCK_TYPES.CODE,
            language: 'js',
            filename: 'consent-update.js',
            code: "gtag('set', 'ads_data_redaction', !categories.marketing);\ngtag('consent', 'update', {\n  analytics_storage: categories.analytics ? 'granted' : 'denied',\n  ad_storage: categories.marketing ? 'granted' : 'denied',\n  ad_user_data: categories.marketing ? 'granted' : 'denied',\n  ad_personalization: categories.marketing ? 'granted' : 'denied',\n  functionality_storage: categories.preferences ? 'granted' : 'denied',\n  personalization_storage: categories.preferences ? 'granted' : 'denied',\n  security_storage: 'granted',\n});",
          },
          {
            id: 'gcmd-verify',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "To check those signals, use [[Tag Assistant|https://tagassistant.google.com/]] or Google's [[verification guidance|https://support.google.com/tagmanager/answer/14218557?hl=en]]. The earliest Consent event should show the default, and the latest should show the update after a banner action.",
          },
          {
            id: 'gcmd-timing-off',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'info',
            label: 'When the setting is off',
            text: 'Cookie Armor emits no consent writes. Google Consent Mode hosts are still not held as scripts. [[How blocking works|how-blocking-works]]',
          },
        ],
      },
      {
        id: 'gcmd-hosts',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Hosts that are not held',
        icon: 'shield-off',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'gcmd-hosts-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'Scripts on `googletagmanager.com`, `google-analytics.com`, `googleadservices.com`, `googlesyndication.com`, and `doubleclick.net`, including subdomains, are never held.',
          },
          {
            id: 'gcmd-hosts-p2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'You place Google tags after the Cookie Armor tag so they see the denied default first. That order is yours.',
          },
          {
            id: 'gcmd-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If the site changes pages without a full load, read [[Single-page apps|single-page-apps]].',
          },
        ],
      },
    ],
  },

  'content-security-policy': {
    id: 'content-security-policy',
    title: 'Content Security Policy',
    description:
      'If the site already sends a Content Security Policy, allow the Cookie Armor CDN, https://consent1.cookiearmor.com, and https://storage.googleapis.com before you install the tag.',
    keywords: [
      'CSP',
      'Content Security Policy',
      'script-src',
      'connect-src',
      'img-src',
      'style-src',
      'unsafe-inline',
      'nonce',
      'cdn.cookiearmor.com',
      'consent1.cookiearmor.com',
      'storage.googleapis.com',
      'failed scan',
    ],
    lastUpdated: '2026-09-27',
    content: [
      {
        id: 'csp-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Read this only if the site already sends a **Content Security Policy (CSP)**. A CSP tells the browser which origins may run scripts, open connections, load images, or apply styles.',
      },
      {
        id: 'csp-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'If these origins are missing, the generated tag never runs, even when the HTML is correct. Allow them before you [[install the script|install-the-script]].',
      },
      {
        id: 'csp-intro-3',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: "A CSP applies in the visitor's browser. A failed scan is a different job: [[Fix a failed scan|fix-a-failed-scan]].",
      },
      {
        id: 'csp-hosts',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Origins to allow',
        icon: 'shield-check',
        color: FAQ_SECTION_ICON_TONES.POSITIVE,
        blocks: [
          {
            id: 'csp-hosts-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Directive', 'Origin', 'Why'],
            rows: [
              ['`script-src`', '`https://cdn.cookiearmor.com`', 'The install tag.'],
              [
                '`connect-src`',
                '`https://cdn.cookiearmor.com`',
                'Connections to the Cookie Armor CDN.',
              ],
              [
                '`connect-src`',
                '`https://consent1.cookiearmor.com`',
                "Recording the visitor's choice.",
              ],
              ['`img-src`', '`https://storage.googleapis.com`', 'Cookie Armor images.'],
            ],
            caption:
              'Origins the visitor browser must allow so Cookie Armor can load and record a choice.',
          },
          {
            id: 'csp-hosts-code',
            type: FAQ_BLOCK_TYPES.CODE,
            language: 'html',
            filename: 'csp-header-example.txt',
            code: "Content-Security-Policy: default-src 'self'; script-src 'self' https://cdn.cookiearmor.com; connect-src 'self' https://cdn.cookiearmor.com https://consent1.cookiearmor.com; img-src 'self' https://storage.googleapis.com; style-src 'self' 'unsafe-inline'",
          },
        ],
      },
      {
        id: 'csp-inline',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Nonce and style',
        icon: 'lock',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'csp-inline-p',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: "The install tag is an external script. Cookie Armor's own files do not need a nonce or a hash.",
          },
          {
            id: 'csp-inline-activate',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If `script-src` does not allow `unsafe-inline`, keep the `nonce` on **your** inline scripts. After a visitor grants a category, those scripts still have to satisfy your policy or the browser blocks them.',
          },
          {
            id: 'csp-inline-activate-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'An external `<script src>` follows `script-src` for that vendor origin.',
          },
          {
            id: 'csp-inline-style',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'The banner and cookie button need inline CSS. A `style-src` that rejects inline styles can hide or unstyle them even when the script loaded. Allow `unsafe-inline` on `style-src`, or accept an unstyled banner.',
          },
          {
            id: 'csp-inline-style-3',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'See [[CSP on MDN|https://developer.mozilla.org/en-US/docs/Web/HTTP/Guides/CSP]]. When these origins are allowed, place the tag. [[Install the script|install-the-script]]',
          },
        ],
      },
    ],
  },

  'single-page-apps': {
    id: 'single-page-apps',
    title: 'Single-page apps',
    description:
      'Cookie Armor boots once in the first document, client-side route changes do not reread stored consent, and newly added tags are still handled.',
    keywords: [
      'SPA',
      'single-page app',
      'client-side routing',
      'pushState',
      'popstate',
      'hashchange',
      'route change',
    ],
    lastUpdated: '2026-09-27',
    content: [
      {
        id: 'spa-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'A **single-page app (SPA)** changes the visible page without a full document load, usually with `history.pushState`. Cookie Armor boots once with the first document and stays until a full page load.',
      },
      {
        id: 'spa-intro-2',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Place the generated tag once, in that first document. [[Install the script|install-the-script]]',
      },
      {
        id: 'spa-runs',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'What a route change repeats',
        icon: 'history',
        color: FAQ_SECTION_ICON_TONES.VIOLET,
        blocks: [
          {
            id: 'spa-runs-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['On a client route change', 'Happens?'],
            rows: [
              ['Cookie Armor boots again', 'No. Boot once in the first document.'],
              [
                'Stored consent is re-read',
                'No. The choice already in memory for this page load remains.',
              ],
              [
                'The banner opens again for a returning visitor',
                'No. The cookie button stays if it was shown.',
              ],
              [
                'New `<script>`, `<iframe>`, or `<img>` nodes are handled',
                'Yes. Nodes added after Cookie Armor has started are still handled.',
              ],
              [
                'The stored choice applies to other paths on this origin',
                'Yes. A different host is a different store. [[JavaScript API|javascript-api]]',
              ],
            ],
            caption: 'Cookie Armor stays with the first document until a full navigation.',
          },
          {
            id: 'spa-runs-note',
            type: FAQ_BLOCK_TYPES.NOTE,
            icon: 'alert-triangle',
            label: 'Do not add the tag again',
            text: 'Do not inject the tag on each client route.\nReplacing the `<html>` element disconnects Cookie Armor.\nA full document load is what boots it again.',
          },
          {
            id: 'spa-next',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If the live site still does not match this, start from the symptom. [[Troubleshoot the script|developer-troubleshooting]]',
          },
        ],
      },
    ],
  },

  'developer-troubleshooting': {
    id: 'developer-troubleshooting',
    title: 'Troubleshoot the script',
    description:
      'Match a live-site symptom to a likely cause, one check, and the article that owns the fix.',
    keywords: [
      'script not loading',
      'banner not appearing',
      'banner not showing',
      'tags still firing',
      'scripts not blocked',
      'consent not persisting',
      'consent not saving',
      'GTM conflict',
      'Google Tag Manager',
      'cookies still present',
      'storage not cleared',
      'HttpOnly',
      'domain is not allowed',
      'additional domains',
      'alias domain',
      'no banner',
      'Rest of the World',
      'duplicate script',
      'inline gtag',
      'single-page app',
      'iframe loads before consent',
      'pixel fires before consent',
      'data-cookiearmor-src',
    ],
    lastUpdated: '2026-10-26',
    content: [
      {
        id: 'dt-intro',
        type: FAQ_BLOCK_TYPES.PARAGRAPH,
        text: 'Start from the symptom. Use the likely cause, run the check, then open the owner. This page does not repeat those articles.',
      },
      {
        id: 'dt-index',
        type: FAQ_BLOCK_TYPES.SECTION,
        title: 'Symptom index',
        icon: 'help-circle',
        color: FAQ_SECTION_ICON_TONES.WARNING,
        blocks: [
          {
            id: 'dt-index-table',
            type: FAQ_BLOCK_TYPES.TABLE,
            headers: ['Symptom', 'Likely cause', 'Check', 'Owner'],
            rows: [
              [
                'Tag missing, or other tags already ran',
                'The generated tag is absent from `<head>`, or it is not first.',
                'One generated tag, first in `<head>`, with no `async`, `defer`, or tag manager.',
                '[[Install the script|install-the-script]]',
              ],
              [
                'The tag appears more than once',
                'The page or a tag manager adds a second copy.',
                'Leave one tag in the first document and remove the rest.',
                '[[Install the script|install-the-script]] · [[Single-page apps|single-page-apps]]',
              ],
              [
                'The tag is in the HTML and the browser blocks it',
                'The Content Security Policy does not allow a required origin.',
                'Compare `script-src`, `connect-src`, and `img-src` with the origin list.',
                '[[Content Security Policy|content-security-policy]]',
              ],
              [
                '**Script Installed**, but another host shows no banner or the console says the domain is not allowed',
                '**Verify installation** and **Recheck installation** crawl the primary **Website URL** only.',
                'Confirm the page host is the primary URL or an **Additional Domain**. **Script Installed** does not cover other hosts.',
                '[[Install the script|install-the-script]] · [[Create a banner|create-a-banner]]',
              ],
              [
                'The tag started, and visitors still see no banner',
                'Rest of the World may be **No banner** for their country.',
                '`window.__cookiearmor__` exists. Treat this as a regulation choice, not a failed install.',
                '[[Choose your regulations|choose-your-regulations]]',
              ],
              [
                'A choice does not come back on the next visit',
                'Storage is host-only, or Cookie Notice **Close** saved nothing.',
                'Look for a stored record on this exact host for the active regulation.',
                '[[JavaScript API|javascript-api]]',
              ],
              [
                'The listener does not run on a return visit',
                'Stored choices are not replayed.',
                'Call `getConsent()` after startup, and keep `onConsentChange()` for the next submission. Do not use `hasConsented()` to decide whether optional tags may run.',
                '[[JavaScript API|javascript-api]]',
              ],
              [
                'A tag still runs after reject, and it has no category',
                'An unclassified tag is allowed.',
                'Confirm a scan category or a valid `data-cookiearmor-category`.',
                '[[How blocking works|how-blocking-works]]',
              ],
              [
                'A Necessary tag still runs',
                'Necessary is never held.',
                'Change the category only when the tag is not essential.',
                '[[How blocking works|how-blocking-works]]',
              ],
              [
                'A Google tag still loads after reject',
                'Google Consent Mode hosts are never held as scripts.',
                'Control them with Consent Mode, and place them after Cookie Armor.',
                '[[How blocking works|how-blocking-works]] · [[Google Consent Mode for developers|google-consent-mode-developers]]',
              ],
              [
                'An inline `gtag` config still runs',
                'That inline `gtag` config is never held.',
                'Do not expect `data-cookiearmor-category` to hold it.',
                '[[How blocking works|how-blocking-works]]',
              ],
              [
                'A tag ran before it could be held',
                'It executed before Cookie Armor started.',
                'Move the generated tag above it and remove `async` and `defer`.',
                '[[Install the script|install-the-script]]',
              ],
              [
                'An iframe or pixel written in the HTML loads before consent',
                'It has a live `src`, so the browser requested it before Cookie Armor could hold it.',
                'Move the address to `data-cookiearmor-src`, remove `src`, and keep the category.',
                '[[Annotate your scripts|annotate-your-scripts]]',
              ],
              [
                'Tracking inside your own JavaScript still runs',
                'Code bundled into your script is not a separate tag.',
                'Gate that code with `getConsent` or `onConsentChange`.',
                '[[How blocking works|how-blocking-works]] · [[JavaScript API|javascript-api]]',
              ],
              [
                'Tags still load after Cookie Notice **Close**',
                'Cookie Notice does not hold tags, and **Close** does not store a choice.',
                'Confirm the banner is Cookie Notice, then read how **Close** and **I Agree** differ.',
                '[[Choose your regulations|choose-your-regulations]]',
              ],
              [
                'Cookies or storage remain after a denied category',
                'The leftover is **HttpOnly**, or it lives on another origin.',
                'Confirm it is in the scan results for this origin and is not HttpOnly.',
                '[[JavaScript API|javascript-api]]',
              ],
              [
                'Google tags send ads or analytics before a choice',
                'Those tags ran before the denied default, or Consent Mode is off and Cookie Armor emits no consent writes.',
                'Place Google tags after Cookie Armor, and confirm the setting is on.',
                '[[Google Consent Mode for developers|google-consent-mode-developers]]',
              ],
              [
                'A client-side route does not replay the banner or the stored choice',
                'A route change does not boot Cookie Armor again and does not reread stored consent.',
                'Expect newly added script, iframe, and image nodes to still be handled. Do not add the tag per route.',
                '[[Single-page apps|single-page-apps]]',
              ],
            ],
            caption: 'One likely cause, one check, and one owner per symptom.',
          },
          {
            id: 'dt-index-note',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'After a successful **Create**, Overview **Save**, or Behaviour **Publish changes**, production updates immediately. **Verify installation** and **Recheck installation** then crawl the primary **Website URL** again. [[Install the script|install-the-script]]',
          },
          {
            id: 'dt-index-note-2',
            type: FAQ_BLOCK_TYPES.PARAGRAPH,
            text: 'If that fresh check still does not explain the symptom, [[contact support|contact-support]].',
          },
        ],
      },
    ],
  },
}
